import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_USERS } from '@/mocks/directories'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { Colleague } from '@/types/domain'

/* The staff roster, for every picker that offers a colleague.
 *
 * ── Why this exists beside useUsers ──────────────────────────────────────────
 *
 * `useUsers` (`features/settings/api`) reads /admin/users, which the server
 * gates on admin.users.manage.
 * That is correct for the user-management screen it was written for, and wrong
 * for a picker: staffing a record is an ordinary act by an ordinary user, so a
 * Sales user opening the colleague dropdown got a 403 and an empty list. The
 * failure is quiet — an empty picker reads as "nobody to pick" rather than as a
 * permission error — which is how it survived on the case team long enough to
 * be inherited here.
 *
 * So this reads GET /api/users: session-guarded, no capability, and a
 * deliberately narrow payload (name, role, office). See the Colleague type.
 *
 * ── What the caller does NOT have to do ──────────────────────────────────────
 *
 * Filter out deactivated accounts. The server returns only accounts that can
 * actually be staffed (FR-57), so a picker rendering this list cannot offer a
 * choice the write would then refuse.
 */
export function useColleagues(
  /** For a picker that lives in a sheet most readers never open — the client
   *  page's "Change sales contact" — so the roster is not pulled on every page
   *  load for it. Omitted means enabled, which every older caller relies on. */
  { enabled }: { enabled?: boolean } = {},
) {
  return useQuery<Colleague[]>({
    queryKey: queryKeys.colleagues(),
    enabled,
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_COLLEAGUES
        : (await api.get<{ items: Colleague[] }>('/users')).items,
    /* The roster changes when somebody joins or leaves the company, which is
       not on the timescale of a form. Kept fresh for five minutes so opening
       four quotes in a row does not refetch the same nine people four times. */
    staleTime: 5 * 60 * 1000,
  })
}

/* The fixture roster, projected from the user-management fixtures rather than
   written out again: two lists of the same nine people drift, and the one that
   drifts is always the one no screen looks at. Deactivated accounts are dropped
   here because the real endpoint drops them server-side — a mock that offered
   them would let a picker bug pass review. */
const MOCK_COLLEAGUES: Colleague[] = MOCK_USERS.filter((user) => user.isActive).map(
  (user) => ({
    id: user.id,
    name: user.name,
    role: user.role,
    office: user.office,
  }),
)
